import { useState } from "react";
import { Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdminLogin } from "@/components/admin/AdminLogin";
import {
  BrandSection, ConversionSection, ScoringSection, ConsentSection,
  PrivacySectionsEditor, ActionsLibrarySection,
} from "@/components/admin/AdminSections";
import { FunnelStatsSection } from "@/components/admin/FunnelStatsSection";
import { ObjectiveSection } from "@/components/admin/ObjectiveSection";
import { RelanceSection } from "@/components/admin/RelanceSection";
import { WeeklyReportSection } from "@/components/admin/WeeklyReportSection";
import { SecuritySection } from "@/components/admin/SecuritySection";
import { MaintenanceSection } from "@/components/admin/MaintenanceSection";
import { MonthlyReportsSection } from "@/components/admin/MonthlyReportsSection";
import { FitConfigSection } from "@/components/admin/FitConfigSection";
import { useAdminSettings } from "@/hooks/useAdminSettings";

// Securite : la cle administrateur est conservee UNIQUEMENT en memoire
// (jamais en sessionStorage ni localStorage). Elle doit etre saisie a chaque
// ouverture de la page.
export default function AdminSettingsPage() {
  const [keyInput, setKeyInput] = useState("");
  const {
    settings, saving, authError, loadSettings, handleSave, adminKey,
    brand, patchBrand,
    conversion, patchConversion,
    scoring, patchScoring,
    consents, patchConsents,
    privacySections, updateSection,
    actionsText, patchAction,
    relance, patchRelance, patchRelanceEmail, patchRelanceDelay, patchRelanceCancellation,
    weeklyReport, patchWeeklyReport,
    objective, patchObjective,
    fit, patchFit,
  } = useAdminSettings();

  return (
    <div className="min-h-screen flex flex-col">
      <Header minimal />
      <main className="flex-1 max-w-[640px] w-full mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-2 text-[#235277]">
          <Settings2 className="w-5 h-5" aria-hidden="true" />
          <span className="text-xs font-semibold tracking-[0.15em] uppercase">Administration</span>
        </div>
        <h1 className="font-heading mt-3 text-2xl font-semibold tracking-tight text-[#060606]">Paramètres et contenus</h1>

        {!settings ? (
          <AdminLogin
            keyInput={keyInput}
            setKeyInput={setKeyInput}
            authError={authError}
            onSubmit={() => loadSettings(keyInput)}
          />
        ) : (
          <div className="mt-8 space-y-6">
            <Alert className="bg-[rgba(226,246,255,0.55)] border-[rgba(35,82,119,0.18)] rounded-xl">
              <AlertDescription className="text-xs leading-relaxed text-[#333333]">
                Aucune valeur n’est codée en dur : ces paramètres et contenus alimentent directement le
                parcours public. Les clés et accès sensibles ne transitent jamais par cette page, ils
                restent côté serveur dans les variables d’environnement.
              </AlertDescription>
            </Alert>

            <FunnelStatsSection adminKey={adminKey} />
            <MonthlyReportsSection adminKey={adminKey} />
            <SecuritySection adminKey={adminKey} />
            <MaintenanceSection adminKey={adminKey} maintenance={settings.maintenance} logoUrl={settings.logo_url} />
            <ObjectiveSection objective={objective} onPatch={patchObjective} adminKey={adminKey} />
            <BrandSection brand={brand} onPatch={patchBrand} />
            <ConversionSection conversion={conversion} onPatch={patchConversion} />
            <FitConfigSection fit={fit} onPatch={patchFit} />
            <ScoringSection scoring={scoring} onPatch={patchScoring} />
            <ConsentSection consents={consents} onPatch={patchConsents} />
            <RelanceSection
              relance={relance}
              onPatch={patchRelance}
              onPatchEmail={patchRelanceEmail}
              onPatchDelay={patchRelanceDelay}
              onPatchCancellation={patchRelanceCancellation}
              adminKey={adminKey}
            />
            <WeeklyReportSection
              weeklyReport={weeklyReport}
              onPatch={patchWeeklyReport}
              adminKey={adminKey}
            />
            <PrivacySectionsEditor privacySections={privacySections} onUpdateSection={updateSection} />
            <ActionsLibrarySection actionsText={actionsText} onPatchAction={patchAction} />

            <Button
              data-testid="admin-save-settings-button"
              onClick={handleSave}
              disabled={saving}
              className="w-full min-h-[52px] rounded-xl bg-[#235277] hover:bg-[#1E4767] font-heading font-semibold transition-colors duration-200"
            >
              {saving ? "Enregistrement…" : "Enregistrer tous les paramètres"}
            </Button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
